import type {
  ContainerRouteStop,
  ContainerStopRole,
} from "../types/containerOrders";
import { isValidPhoneFormat } from "./phone";

/** The part of a stop a complaint belongs under. */
export type StopIssueField =
  | "company"
  | "street"
  | "city"
  | "location"
  | "skipReason"
  | "contactPhone";

/** Message keys, keyed by the box that has to change. Empty when the stop is fine. */
export type StopIssues = Partial<Record<StopIssueField, string>>;

/** Every stop's issues, keyed by the stop's own id. */
export type StopIssuesById = Record<string, StopIssues>;

/**
 * The same sentences the order form uses, per role.
 *
 * Reused verbatim rather than reworded, because they describe the same rule
 * about the same stops — a dispatcher moving between the order and one of its
 * containers should not have to work out whether two differently-phrased
 * complaints are the same complaint. `selection` and `name` both sit under the
 * company box: they are two ways of failing to name a directory record.
 */
const MESSAGES: Record<
  ContainerStopRole,
  { selection: string; name: string; street: string; city: string; location: string }
> = {
  terminalPickup: {
    selection: "validation:containerOrder.pickupTerminalSelectionRequired",
    name: "validation:containerOrder.pickupTerminalRequired",
    street: "validation:containerOrder.pickupAddressRequired",
    city: "validation:containerOrder.pickupCityRequired",
    location: "validation:containerOrder.pickupLocationRequired",
  },
  loadingSite: {
    selection: "validation:containerOrder.loadingSiteSelectionRequired",
    name: "validation:containerOrder.loadingSiteRequired",
    street: "validation:containerOrder.loadingAddressRequired",
    city: "validation:containerOrder.loadingCityRequired",
    location: "validation:containerOrder.loadingLocationRequired",
  },
  terminalDropoff: {
    selection: "validation:containerOrder.dropoffTerminalSelectionRequired",
    name: "validation:containerOrder.dropoffTerminalRequired",
    street: "validation:containerOrder.dropoffAddressRequired",
    city: "validation:containerOrder.dropoffCityRequired",
    location: "validation:containerOrder.dropoffLocationRequired",
  },
  /**
   * An added stop is held to less, and the messages say what instead of who.
   *
   * There is no `selection` among them because an added stop needs no directory
   * record: a certified scale on an industrial estate, a customs window or a
   * lay-by the tachograph forced are all places a truck is genuinely sent to and
   * none of them is in anyone's address book. What it does need is what dispatch
   * actually depends on — a name to call it by, a street, a city, and the map
   * location that only a pick from the suggestions can produce.
   */
  custom: {
    selection: "validation:containerOrder.stopNameRequired",
    name: "validation:containerOrder.stopNameRequired",
    street: "validation:containerOrder.stopAddressRequired",
    city: "validation:containerOrder.stopCityRequired",
    location: "validation:containerOrder.stopLocationRequired",
  },
};

/**
 * The anchors that must prove they were picked from the directory, and the id
 * that proves it.
 *
 * All three resolve into Addresses — a pickup terminal, a client's loading site
 * and a drop-off terminal are all places a truck is sent to, which is the one
 * thing the directory of places is for.
 *
 * Added stops are absent on purpose: their link is optional, so there is no id
 * whose absence would be a fault.
 */
const ID_FIELD: Partial<Record<ContainerStopRole, "addressId">> = {
  terminalPickup: "addressId",
  loadingSite: "addressId",
  terminalDropoff: "addressId",
};

const blank = (value?: string | null) => !value || !value.trim();

/**
 * What is still missing from one stop of a container's route.
 *
 * The mirror of `routeStopValidator` on the API, which is the rule that
 * actually holds. Two things decide how much is asked of a stop:
 *
 *  · whether it is being made at all. A stop turned off has no truck going to
 *    it, so demanding a directory record and a map location for it would be
 *    asking the dispatcher to describe a place nobody is driving to before they
 *    are allowed to say nobody is driving to it. What it needs instead is the
 *    reason, which is the whole point of the toggle: it reaches the driver.
 *  · what part it plays. An anchor must resolve to a directory record, because
 *    a whole batch is dispatched to it and the record is where its coordinates
 *    and its contact live. An added stop need not.
 *
 * The map location is the one that cannot be typed. It only exists if the
 * address came from the Places dropdown, or from a directory record whose own
 * address did — which is what makes "pick it from the suggestions" the whole
 * instruction.
 */
export function findStopIssues(stop: ContainerRouteStop): StopIssues {
  const messages = MESSAGES[stop.role] ?? MESSAGES.custom;
  const issues: StopIssues = {};

  if (stop.enabled === false) {
    if (blank(stop.skipReason)) {
      issues.skipReason = "validation:containerOrder.skipReasonRequired";
    }
    return issues;
  }

  if (blank(stop.companyName)) {
    issues.company = messages.name;
  } else {
    const idField = ID_FIELD[stop.role];
    // A name with no id is a name typed before the picker enforced selection;
    // re-picking it from the directory backfills the id, the address and the
    // location together. Added stops have no such requirement.
    if (idField && !stop[idField]) issues.company = messages.selection;
  }

  if (blank(stop.address?.street)) issues.street = messages.street;
  if (blank(stop.address?.city)) issues.city = messages.city;

  const coordinates = stop.address?.coordinates;
  if (
    typeof coordinates?.latitude !== "number" ||
    typeof coordinates?.longitude !== "number"
  ) {
    issues.location = messages.location;
  }

  if (!isValidPhoneFormat(stop.contactPhone)) {
    issues.contactPhone = "validation:partner.phoneInvalid";
  }

  return issues;
}

/** Every stop's issues at once, keyed by the stop's own id. */
export function findAllStopIssues(
  stops: ContainerRouteStop[],
): StopIssuesById {
  const byId: StopIssuesById = {};
  for (const stop of stops) byId[String(stop._id)] = findStopIssues(stop);
  return byId;
}

/**
 * The first thing wrong anywhere on the route, for the save's own message.
 *
 * Walked in route order rather than over the object's keys, so the sentence the
 * user is shown is about the earliest stop that needs attention — which is
 * where they will start looking anyway.
 */
export function firstStopIssue(
  stops: ContainerRouteStop[],
  issues: StopIssuesById,
): string | undefined {
  for (const stop of stops) {
    const found = Object.values(issues[String(stop._id)] ?? {})[0];
    if (found) return found;
  }
  return undefined;
}

/** Whether the route has anything outstanding at all. */
export const hasStopIssues = (issues: StopIssuesById): boolean =>
  Object.values(issues).some((stop) => Object.keys(stop).length > 0);
